import Button from "components/button";
import { IEditOrgForm, IOrgDetail } from "interfaces";
import { OrganizationInformationSection } from "./organizationInformationSection";
import { OwnerAccountSection } from "./ownerAccountSection";
import { useForm } from "react-hook-form";
import { HttpError, useUpdate } from "@refinedev/core";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { REGEX_DOMAIN, REGEX_VALID_EMAIL } from "utills/constants";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { FullScreenLoading } from "components/fullScreenLoading";
import { SellerIdentificationSection } from "./sellerIdentificationSection";
import validator from "validator";
import { isEmpty } from "lodash";
import { isValidURL } from "utills/helpers";
import { WhitelabelSection } from "./whitelabelSection";
import { FipixWhitelabelSection } from "./fipixWhitelabelSection";

interface EditOrganizationFormProps {
  id: string;
  organization?: IOrgDetail;
}

const EditOrganizationSchema = z
  .object({
    name_vi: z
      .string()
      .min(1, "This field is required")
      .max(100, "Text length is too long"),
    name_en: z
      .string()
      .min(1, "This field is required")
      .max(100, "Text length is too long"),
    is_active: z.boolean(),
    country: z.string().min(1, "This field is required"),
    organization_slug: z
      .string()
      .min(1, "This field is required")
      .max(100, "Text length is too long")
      .refine((value) => REGEX_DOMAIN.test(value ?? ""), "Invalid slug"),
    homepage_position: z.union([
      z.literal(0),
      z.coerce.number().gte(0, "Must be 18 and above").lte(6),
    ]),
    introduction_en: z.string().nullish(),
    introduction_vi: z.string().nullish(),
    contact_phone_area_code: z.string(),
    contact_phone: z
      .union([
        z.literal(""),
        z.string().refine(validator.isMobilePhone, "Invalid phone number"),
      ])
      .nullish(),
    contact_email: z
      .union([
        z.literal(""),
        z
          .string()
          .refine(
            (value) => REGEX_VALID_EMAIL.test(value ?? ""),
            "Invalid email address"
          ),
      ])
      .nullish(),
    cover_banner: z.string().nullish(),
    organization_logo: z.string().nullish(),
    organization_banner: z.string().nullish(),
    square_promotion_banner: z.string().nullish(),
    rectangular_promotion_banner: z.string().nullish(),
    brand_logo: z.string().nullish(),
    brand_color: z.string().nullish(),
    event_type: z.array(z.string()).min(1, "This field is required"),
    seller_company_name: z.string().min(1, "This field is required"),
    seller_company_email: z
      .string()
      .min(1, "This field is required")
      .refine(
        (value) => REGEX_VALID_EMAIL.test(value ?? ""),
        "Invalid email address"
      ),
    seller_company_phone: z
      .string()
      .min(1, "This field is required")
      .refine(validator.isMobilePhone, "Invalid phone number"),
    seller_company_phone_area_code: z.string().min(1, "This field is required"),
    seller_company_tax: z.string().min(1, "This field is required"),
    seller_place_of_issue: z.string(),
    seller_business_address: z.string().min(1, "This field is required"),
    seller_business_license: z.array(z.string()),
    seller_business_certificate_number: z
      .string()
      .min(1, "This field is required"),
    seller_business_issue_date: z
      .string()
      .nullable()
      .transform((value) => (value === "" ? null : value)),
    use_whitelabel_integration: z.boolean(),
    whitelabel_domain: z.string(),
    whitelabel_favicon: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_brand_logo_url: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_email_footer: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    use_whitelabel_signin_signup: z.boolean(),
    use_whitelabel_orders: z.boolean(),
    whitelabel_sender_email: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_email_footer_text_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_menu: z.array(
      z.object({
        url: z.string(),
        name_en: z.string(),
        name_vi: z.string(),
      })
    ),
    whitelabel_header_text_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_footer_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_footer_text_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    whitelabel_left_website_footer_content_vi: z.string(),
    whitelabel_left_website_footer_content_en: z.string(),
    whitelabel_right_website_footer_content_vi: z.string(),
    whitelabel_right_website_footer_content_en: z.string(),
    use_fipix_integration: z.boolean(),
    fipix_domain: z.string(),
    fipix_brand_logo_url: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_menu: z.array(
      z.object({
        url: z.string(),
        name_en: z.string(),
        name_vi: z.string(),
      })
    ),
    fipix_seo_title: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_seo_description: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_seo_og_image: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_favicon: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_background_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_second_background_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_main_text_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_header_background_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_menu_text_color: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_footer_html_vi: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
    fipix_footer_html_en: z
      .string()
      .transform((value) =>
        value === null || value === undefined ? "" : value
      ),
  })
  .superRefine(
    (
      {
        whitelabel_sender_email,
        use_whitelabel_integration,
        whitelabel_domain,
        whitelabel_brand_logo_url,
        whitelabel_menu,
        use_fipix_integration,
        fipix_domain,
        fipix_brand_logo_url,
        fipix_menu,
        fipix_seo_title,
        fipix_seo_description,
        fipix_seo_og_image,
        fipix_favicon,
      },
      ctx
    ) => {
      if (use_whitelabel_integration) {
        if (!isEmpty(whitelabel_sender_email)) {
          if (!REGEX_VALID_EMAIL.test(whitelabel_sender_email)) {
            ctx.addIssue({
              path: ["whitelabel_sender_email"],
              code: "custom",
              message: "Invalid email address",
            });
          }
        }
        if (
          !isEmpty(whitelabel_brand_logo_url) &&
          !isValidURL(whitelabel_brand_logo_url)
        ) {
          ctx.addIssue({
            path: ["whitelabel_brand_logo_url"],
            code: "custom",
            message: "Please enter a valid URL",
          });
        }
        if (isEmpty(whitelabel_domain)) {
          ctx.addIssue({
            path: ["whitelabel_domain"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (!isEmpty(whitelabel_domain) && !isValidURL(whitelabel_domain)) {
          ctx.addIssue({
            path: ["whitelabel_domain"],
            code: "custom",
            message: "Please enter a valid URL",
          });
        }
        if (!isEmpty(whitelabel_menu)) {
          whitelabel_menu.forEach((menu, index) => {
            if (!isEmpty(menu.url) && !isValidURL(menu.url)) {
              ctx.addIssue({
                path: [`whitelabel_menu.${index}.url`],
                code: "custom",
                message: "Please enter a valid URL",
              });
            }
            if (isEmpty(menu.name_en)) {
              ctx.addIssue({
                path: [`whitelabel_menu.${index}.name_en`],
                code: "custom",
                message: "This field is required",
              });
            }
            if (isEmpty(menu.name_vi)) {
              ctx.addIssue({
                path: [`whitelabel_menu.${index}.name_vi`],
                code: "custom",
                message: "This field is required",
              });
            }
          });
        }
      }
      if (use_fipix_integration) {
        if (isEmpty(fipix_domain)) {
          ctx.addIssue({
            path: ["fipix_domain"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (!isEmpty(fipix_domain) && !REGEX_DOMAIN.test(fipix_domain)) {
          ctx.addIssue({
            path: ["fipix_domain"],
            code: "custom",
            message: "Invalid domain",
          });
        }
        if (isEmpty(fipix_brand_logo_url)) {
          ctx.addIssue({
            path: ["fipix_brand_logo_url"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (
          !isEmpty(fipix_brand_logo_url) &&
          !isValidURL(fipix_brand_logo_url)
        ) {
          ctx.addIssue({
            path: ["fipix_brand_logo_url"],
            code: "custom",
            message: "Please enter a valid URL",
          });
        }
        if (isEmpty(fipix_seo_title)) {
          ctx.addIssue({
            path: ["fipix_seo_title"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (isEmpty(fipix_seo_description)) {
          ctx.addIssue({
            path: ["fipix_seo_description"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (isEmpty(fipix_seo_og_image)) {
          ctx.addIssue({
            path: ["fipix_seo_og_image"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (!isEmpty(fipix_seo_og_image) && !isValidURL(fipix_seo_og_image)) {
          ctx.addIssue({
            path: ["fipix_seo_og_image"],
            code: "custom",
            message: "Please enter a valid URL",
          });
        }
        if (isEmpty(fipix_favicon)) {
          ctx.addIssue({
            path: ["fipix_favicon"],
            code: "custom",
            message: "This field is required",
          });
        }
        if (!isEmpty(fipix_favicon) && !isValidURL(fipix_favicon)) {
          ctx.addIssue({
            path: ["fipix_favicon"],
            code: "custom",
            message: "Please enter a valid URL",
          });
        }
        if (!isEmpty(fipix_menu)) {
          fipix_menu.forEach((menu, index) => {
            if (isEmpty(menu.url)) {
              ctx.addIssue({
                path: [`fipix_menu.${index}.url`],
                code: "custom",
                message: "This field is required",
              });
            }
            if (!isEmpty(menu.url) && !isValidURL(menu.url)) {
              ctx.addIssue({
                path: [`fipix_menu.${index}.url`],
                code: "custom",
                message: "Please enter a valid URL",
              });
            }
            if (isEmpty(menu.name_en)) {
              ctx.addIssue({
                path: [`fipix_menu.${index}.name_en`],
                code: "custom",
                message: "This field is required",
              });
            }
            if (isEmpty(menu.name_vi)) {
              ctx.addIssue({
                path: [`fipix_menu.${index}.name_vi`],
                code: "custom",
                message: "This field is required",
              });
            }
          });
        }
      }
    }
  );

export const EditOrganizationForm = ({
  id,
  organization,
}: EditOrganizationFormProps) => {
  const navigate = useNavigate();

  const methods = useForm<IEditOrgForm, HttpError, IEditOrgForm>({
    defaultValues: {
      name_vi: organization?.name_vi ?? "",
      name_en: organization?.name_en ?? "",
      is_active: organization?.is_active ?? false,
      country: organization?.country ?? "",
      workspace_domain: organization?.workspace_domain ?? "",
      landing_page: organization?.landing_page ?? "",
      organization_slug: organization?.organization_slug ?? "",
      homepage_position: organization?.homepage_position ?? 0,
      introduction_vi: organization?.introduction_vi ?? "",
      introduction_en: organization?.introduction_en ?? "",
      contact_phone_area_code: organization?.contact_phone_area_code ?? "",
      contact_phone: organization?.contact_phone ?? "",
      contact_email: organization?.contact_email ?? "",
      cover_banner: organization?.cover_banner ?? "",
      rectangular_promotion_banner:
        organization?.rectangular_promotion_banner ?? "",
      square_promotion_banner: organization?.square_promotion_banner ?? "",
      organization_banner: organization?.organization_banner ?? "",
      organization_logo: organization?.organization_logo ?? "",
      brand_logo: organization?.brand_logo ?? "",
      brand_color: organization?.brand_color ?? "",
      event_type: organization?.event_type ?? [],
      seller_business_address: organization?.seller_business_address ?? "",
      seller_business_certificate_number:
        organization?.seller_business_certificate_number ?? "",
      seller_business_issue_date:
        organization?.seller_business_issue_date ?? null,
      seller_business_license: organization?.seller_business_license ?? [],
      seller_company_email: organization?.seller_company_email ?? "",
      seller_company_name: organization?.seller_company_name ?? "",
      seller_company_phone: organization?.seller_company_phone ?? "",
      seller_company_phone_area_code:
        organization?.seller_company_phone_area_code ?? "84",
      seller_company_tax: organization?.seller_company_tax ?? "",
      seller_place_of_issue: organization?.seller_place_of_issue ?? "",
      use_whitelabel_integration:
        organization?.use_whitelabel_integration ?? false,
      whitelabel_domain: organization?.whitelabel_domain ?? "",
      whitelabel_favicon: organization?.whitelabel_favicon ?? "",
      whitelabel_brand_logo_url: organization?.whitelabel_brand_logo_url ?? "",
      whitelabel_email_footer: organization?.whitelabel_email_footer ?? "",
      use_whitelabel_signin_signup:
        organization?.use_whitelabel_signin_signup ?? false,
      use_whitelabel_orders: organization?.use_whitelabel_orders ?? false,
      whitelabel_sender_email: organization?.whitelabel_sender_email ?? "",
      whitelabel_email_footer_text_color:
        organization?.whitelabel_email_footer_text_color ?? "#000000",
      whitelabel_menu: organization?.whitelabel_menu ?? [],
      whitelabel_header_text_color:
        organization?.whitelabel_header_text_color ?? "#000000",
      whitelabel_footer_color:
        organization?.whitelabel_footer_color ?? "#000000",
      whitelabel_footer_text_color:
        organization?.whitelabel_footer_text_color ?? "#000000",
      whitelabel_left_website_footer_content_vi:
        organization?.whitelabel_left_website_footer_content_vi ?? "",
      whitelabel_left_website_footer_content_en:
        organization?.whitelabel_left_website_footer_content_en ?? "",
      whitelabel_right_website_footer_content_vi:
        organization?.whitelabel_right_website_footer_content_vi ?? "",
      whitelabel_right_website_footer_content_en:
        organization?.whitelabel_right_website_footer_content_en ?? "",
      use_fipix_integration: organization?.use_fipix_integration ?? false,
      fipix_domain: organization?.fipix_domain ?? "",
      fipix_brand_logo_url: organization?.fipix_brand_logo_url ?? "",
      fipix_menu: organization?.fipix_menu ?? [],
      fipix_seo_title: organization?.fipix_seo_title ?? "",
      fipix_seo_description: organization?.fipix_seo_description ?? "",
      fipix_seo_og_image: organization?.fipix_seo_og_image ?? "",
      fipix_favicon: organization?.fipix_favicon ?? "",
      fipix_background_color: organization?.fipix_background_color ?? "#FFFFFF",
      fipix_second_background_color:
        organization?.fipix_second_background_color ?? "#F5F5F5",
      fipix_main_text_color: organization?.fipix_main_text_color ?? "#111111",
      fipix_header_background_color:
        organization?.fipix_header_background_color ?? "#0A0A0A",
      fipix_menu_text_color: organization?.fipix_menu_text_color ?? "#FFFFFF",
      fipix_footer_html_vi: organization?.fipix_footer_html_vi ?? "",
      fipix_footer_html_en: organization?.fipix_footer_html_en ?? "",
    },
    resolver: zodResolver(EditOrganizationSchema),
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: IEditOrgForm) => {
    const submitValues = {
      ...values,
      introduction_vi: values.introduction_vi,
      introduction_en: values.introduction_en,
    };
    mutate(
      {
        resource: "organization",
        id: id,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/organization/org/${id}`);
        },
      }
    );
  };

  return (
    <form
      className="w-full flex flex-col gap-y-3"
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <OrganizationInformationSection {...methods} />
      <SellerIdentificationSection {...methods} />
      <OwnerAccountSection {...methods} />
      <WhitelabelSection {...methods} />
      <FipixWhitelabelSection {...methods} />
      <div className="w-full px-4 py-4 sm:px-6 bg-white">
        <div className="flex items-center justify-end gap-x-4">
          <Link to={`/organization/org/${id}`}>
            <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
              CANCEL
            </Button>
          </Link>
          <Button className="w-20" type="submit">
            SAVE
          </Button>
        </div>
      </div>
      {isLoading ? <FullScreenLoading /> : <></>}
    </form>
  );
};
